import BaseApi from './BaseApi.js';

/**
 * DiscrepanciesApi - Symfony API for aircraft discrepancies
 * Endpoints: /api/v1/org/{organizationUnique}/discrepancy/...
 */
class DiscrepanciesApi extends BaseApi {
	static get endpoint() {
		return this.buildOrgEndpoint('/discrepancy');
	}

	static _base(orgUnique) {
		return orgUnique ? `/org/${orgUnique}/discrepancy` : this.buildOrgEndpoint('/discrepancy');
	}

	// Get all discrepancies for an aircraft
	static async getAircraftDiscrepancies(aircraftId, orgUnique) {
		const base = orgUnique ? `/org/${orgUnique}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/aircraft/${aircraftId}/discrepancy`);
	}

	// Get single discrepancy details (includes activities array)
	static async getDiscrepancy(discrepancyId, orgUnique) {
		return await this.get(`${this._base(orgUnique)}/${discrepancyId}`);
	}

	// Add new discrepancy. Accepts an optional `orgUniqueOverride` so callers
	// editing a cross-org event (e.g. Calendar Month V2's CheckInScreen on a
	// shared aircraft) target the event's org instead of the URL-resolved
	// org. Falls back to the org from `buildOrgEndpoint(...)` when omitted.
	static async addDiscrepancy(discrepancyData, orgUniqueOverride) {
		const endpoint = orgUniqueOverride
			? `/org/${orgUniqueOverride}/discrepancy`
			: this.endpoint;
		return await this.post(endpoint, discrepancyData);
	}

	// Update discrepancy fields (PUT)
	static async updateDiscrepancy(discrepancyId, discrepancyData, orgUnique) {
		return await this.put(`${this._base(orgUnique)}/${discrepancyId}`, discrepancyData);
	}

	// Delete discrepancy with optional disposition reason
	static async deleteDiscrepancy(discrepancyId, disposition, orgUnique) {
		const qs = disposition ? `?disposition=${encodeURIComponent(disposition)}` : '';
		return await this.delete(`${this._base(orgUnique)}/${discrepancyId}${qs}`);
	}

	// Change discrepancy status (PATCH — separate endpoint from PUT)
	static async changeStatus(discrepancyId, statusData, orgUnique) {
		return await this.patch(`${this._base(orgUnique)}/${discrepancyId}/status`, statusData);
	}

	// Add comment to discrepancy
	static async addComment(discrepancyId, commentData, orgUnique) {
		return await this.post(`${this._base(orgUnique)}/${discrepancyId}/comment`, commentData);
	}

	// Edit discrepancy comment
	static async editComment(activityId, commentData, orgUnique) {
		return await this.put(`${this._base(orgUnique)}/comment/${activityId}`, commentData);
	}

	// Fetch mechanics list for AssignTo dropdown
	static async getMechanics(orgUnique) {
		const base = orgUnique ? `/org/${orgUnique}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/mechanics`);
	}
}

export default DiscrepanciesApi;
